import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  Calendar,
  User,
  AlertCircle,
  Settings,
  Loader2,
  AlertTriangle,
  FileText,
  Edit3,
  Save,
  X,
  DollarSign,
  TrendingUp,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { ExceptionUtils, RuleException } from "@/types";

interface RuleExceptionDetailsProps {
  ruleException: RuleException;
  onBack: () => void;
  onUpdateStatus: (ruleExceptionId: number, status: string) => Promise<boolean>;
  onAssign: (ruleExceptionId: number, assignedTo: string) => Promise<boolean>;
  /**
   * A list of team members that can be assigned to the exception. Typically this will contain
   * email addresses or unique identifiers for each user.
   */
  teamMembers: string[];
  onUpdateNotes?: (ruleExceptionId: number, notes: string) => Promise<boolean>;
}

const getStatusBadge = (status: string) => {
  const statusConfig = {
    New: {
      variant: "secondary" as const,
      className: "bg-slate-100 text-slate-700 hover:bg-slate-200",
    },
    Open: {
      variant: "destructive" as const,
      className: "bg-red-100 text-red-700 hover:bg-red-200",
    },
    "In Progress": {
      variant: "default" as const,
      className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
    },
    Resolved: {
      variant: "default" as const,
      className: "bg-green-100 text-green-700 hover:bg-green-200",
    },
    Waived: {
      variant: "outline" as const,
      className: "bg-blue-100 text-blue-700 hover:bg-blue-200",
    },
  };

  const config =
    statusConfig[status as keyof typeof statusConfig] || statusConfig["New"];

  return (
    <Badge variant={config.variant} className={config.className}>
      {status}
    </Badge>
  );
};

const getRiskLevelBadge = (riskLevel: string) => {
  const riskConfig = {
    Low: {
      variant: "secondary" as const,
      className: "bg-green-100 text-green-700 hover:bg-green-200",
    },
    Medium: {
      variant: "default" as const,
      className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
    },
    High: {
      variant: "default" as const,
      className: "bg-orange-100 text-orange-700 hover:bg-orange-200",
    },
    Critical: {
      variant: "destructive" as const,
      className: "bg-red-100 text-red-700 hover:bg-red-200",
    },
  };

  const config =
    riskConfig[riskLevel as keyof typeof riskConfig] || riskConfig["Low"];

  return (
    <Badge variant={config.variant} className={config.className}>
      {riskLevel}
    </Badge>
  );
};

export const ExceptionDetails: React.FC<RuleExceptionDetailsProps> = ({
  ruleException,
  onBack,
  onUpdateStatus,
  onAssign,
  teamMembers,
  onUpdateNotes,
}) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [newAssignee, setNewAssignee] = useState(
    ruleException.assignedTo || ""
  );
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState(ruleException.notes || "");
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);

  // Check if this is a cash movement exception
  const isCashMovementException =
    ExceptionUtils.isCashMovementException(ruleException);

  const handleStatusUpdate = async (newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      await onUpdateStatus(ruleException.ruleExceptionId, newStatus);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAssign = async () => {
    setIsAssigning(true);
    try {
      await onAssign(ruleException.ruleExceptionId, newAssignee);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleNotesUpdate = async () => {
    if (!onUpdateNotes) return;

    setIsUpdatingNotes(true);
    try {
      const success = await onUpdateNotes(
        ruleException.ruleExceptionId,
        notesText
      );
      if (success) {
        setIsEditingNotes(false);
      }
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  const handleCancelNotesEdit = () => {
    setNotesText(ruleException.notes || "");
    setIsEditingNotes(false);
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return {
        relative: formatDistanceToNow(date, { addSuffix: true }),
        absolute: format(date, "PPpp"),
      };
    } catch (error) {
      return {
        relative: "Invalid date",
        absolute: "Invalid date",
      };
    }
  };

  // Extract cash movement details from description if available
  const extractCashMovementDetails = (description: string) => {
    const patterns = {
      count: /(\d+)\s+unexplained\s+cash\s+movements?/i,
      amount: /totaling\s+[\$€£]?([0-9,]+(?:\.\d{2})?)\s*([A-Z]{3})?/i,
      currency: /([A-Z]{3})/g,
    };

    const countMatch = description.match(patterns.count);
    const amountMatch = description.match(patterns.amount);

    return {
      count: countMatch ? parseInt(countMatch[1]) : null,
      amount: amountMatch ? parseFloat(amountMatch[1].replace(/,/g, "")) : null,
      currency: amountMatch && amountMatch[2] ? amountMatch[2] : "USD",
    };
  };

  const cashDetails = isCashMovementException
    ? extractCashMovementDetails(ruleException.description)
    : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-100">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-slate-600 hover:text-slate-800 hover:bg-slate-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Rule Exceptions
          </Button>
          <div className="h-4 w-px bg-slate-200"></div>
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            <span className="font-medium text-slate-800">
              Rule Exception Details
            </span>
            {isCashMovementException && (
              <Badge
                variant="outline"
                className="bg-orange-100 text-orange-800 border-orange-200"
              >
                High Risk - Financial Compliance
              </Badge>
            )}
          </div>
        </div>
        {getStatusBadge(ruleException.status)}
      </div>

      {/* Cash Movement Alert */}
      {isCashMovementException && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>High-Risk Financial Compliance Exception:</strong> This
            exception relates to unexplained cash movements which may require
            immediate attention and documentation to ensure regulatory
            compliance.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl text-slate-800 flex items-center">
                Exception Information
                {isCashMovementException && (
                  <DollarSign className="h-5 w-5 ml-2 text-orange-600" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">
                  Rule Key
                </Label>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-2 bg-slate-100 rounded-lg text-sm font-mono text-slate-800 border">
                    {ruleException.ruleKey}
                  </code>
                  {isCashMovementException && (
                    <Badge
                      variant="outline"
                      className="text-orange-700 border-orange-200 bg-orange-50"
                    >
                      Cash Movement Detection
                    </Badge>
                  )}
                </div>
              </div>

              {/* Cash Movement Summary - Only for cash movement exceptions */}
              {isCashMovementException && cashDetails && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-orange-800 flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2" />
                      Cash Movement Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      {cashDetails.count && (
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-orange-700">
                            Transactions Affected
                          </Label>
                          <div className="text-2xl font-bold text-orange-800">
                            {cashDetails.count.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {cashDetails.amount && (
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-orange-700">
                            Total Amount
                          </Label>
                          <div className="text-2xl font-bold text-orange-800">
                            {cashDetails.amount.toLocaleString("en-US", {
                              style: "currency",
                              currency: cashDetails.currency,
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded border border-orange-200">
                      <strong>Action Required:</strong> These cash movements
                      require explanation and documentation for compliance
                      purposes. Please review each transaction and provide
                      appropriate justification.
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">
                  Risk Level
                </Label>
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4 text-slate-400" />
                  {getRiskLevelBadge(ruleException.riskLevel)}
                  {isCashMovementException && (
                    <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                      Requires immediate attention
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">
                  Short Description
                </Label>
                <p className="text-slate-800 font-medium leading-relaxed">
                  {ruleException.shortDescription}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">
                  Full Description
                </Label>
                <p
                  className={`text-slate-700 leading-relaxed text-sm p-4 rounded-lg border ${
                    isCashMovementException
                      ? "bg-orange-50 border-orange-200"
                      : "bg-slate-50"
                  }`}
                >
                  {ruleException.description}
                </p>
              </div>

              {/* Enhanced Notes Section for Cash Movement */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-slate-600 flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    {isCashMovementException
                      ? "Compliance Notes & Documentation"
                      : "Notes"}
                  </Label>
                  {onUpdateNotes && !isEditingNotes && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingNotes(true)}
                      className="text-slate-600 hover:text-slate-800 h-8 px-2"
                    >
                      <Edit3 className="h-3 w-3 mr-1" />
                      {ruleException.notes
                        ? "Edit"
                        : isCashMovementException
                        ? "Add Documentation"
                        : "Add Notes"}
                    </Button>
                  )}
                </div>

                {isEditingNotes ? (
                  <div className="space-y-3">
                    <Textarea
                      value={notesText}
                      onChange={(e) => setNotesText(e.target.value)}
                      placeholder={
                        isCashMovementException
                          ? "Document the business justification for these cash movements, including source, purpose, and approval details..."
                          : "Add your notes here..."
                      }
                      className="min-h-[120px] border-slate-200 focus:border-slate-400 resize-none"
                      disabled={isUpdatingNotes}
                    />
                    {isCashMovementException && (
                      <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded border border-orange-200">
                        <strong>Documentation Guidelines:</strong> Include
                        transaction sources, business purposes, approval chains,
                        and any supporting documentation references.
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        onClick={handleNotesUpdate}
                        disabled={
                          isUpdatingNotes || notesText === ruleException.notes
                        }
                      >
                        {isUpdatingNotes ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-3 w-3 mr-1" />
                            {isCashMovementException
                              ? "Save Documentation"
                              : "Save Notes"}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelNotesEdit}
                        disabled={isUpdatingNotes}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : ruleException.notes ? (
                  <div
                    className={`border rounded-lg p-4 ${
                      isCashMovementException
                        ? "bg-orange-50 border-orange-200"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                      {ruleException.notes}
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                    <p className="text-slate-500 text-sm italic">
                      {isCashMovementException
                        ? 'No compliance documentation added yet. Click "Add Documentation" to document the business justification for these cash movements.'
                        : 'No notes added yet. Click "Add Notes" to add your first note.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Existing timestamp section */}
              <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-100">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-600">
                    Created
                  </Label>
                  <div className="flex items-center text-slate-700">
                    <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                    <span
                      className="text-sm"
                      title={formatDateTime(ruleException.createdAt).absolute}
                    >
                      {formatDateTime(ruleException.createdAt).relative}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-600">
                    Last Updated
                  </Label>
                  <div className="flex items-center text-slate-700">
                    <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                    <span
                      className="text-sm"
                      title={formatDateTime(ruleException.updatedAt).absolute}
                    >
                      {formatDateTime(ruleException.updatedAt).relative}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Existing Actions Sidebar */}
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg text-slate-800 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-slate-600" />
                Actions
                {isCashMovementException && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-xs bg-orange-100 text-orange-700 border-orange-200"
                  >
                    Priority
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="text-sm font-medium text-slate-700">
                  Status
                </Label>
                <Select
                  value={ruleException.status}
                  onValueChange={handleStatusUpdate}
                  disabled={isUpdatingStatus}
                >
                  <SelectTrigger className="border-slate-200 focus:border-slate-400">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Resolved">Resolved</SelectItem>
                    <SelectItem value="Waived">Waived</SelectItem>
                  </SelectContent>
                </Select>
                {isUpdatingStatus && (
                  <div className="flex items-center text-sm text-slate-600">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating status...
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium text-slate-700">
                  Assignment
                </Label>
                <div className="space-y-3">
                  <Select value={newAssignee} onValueChange={setNewAssignee}>
                    <SelectTrigger className="border-slate-200 focus:border-slate-400">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((member) => (
                        <SelectItem key={member} value={member}>
                          {member}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={handleAssign}
                    disabled={
                      isAssigning || newAssignee === ruleException.assignedTo
                    }
                    className="w-full"
                  >
                    {isAssigning ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Assigning...
                      </>
                    ) : (
                      "Update Assignment"
                    )}
                  </Button>
                </div>
                {ruleException.assignedTo && (
                  <div className="flex items-center p-3 bg-slate-50 rounded-lg border">
                    <User className="h-4 w-4 mr-2 text-slate-500" />
                    <div className="text-sm">
                      <span className="text-slate-600">
                        Currently assigned to:
                      </span>
                      <br />
                      <span className="font-medium text-slate-800">
                        {ruleException.assignedTo}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
